import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Box, Typography, CircularProgress
} from '@mui/material';
import { createAppointment } from '../../services/appointmentsApi';

function toLocalDatetimeValue(d) {
  // Returns a string that the <input type="datetime-local"> accepts.
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function defaultSlot() {
  // Default: tomorrow at 10:00 local, 50 min long.
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(10, 0, 0, 0);
  return tomorrow;
}

export default function ScheduleCallDialog({ open, onClose, token, clientId, conversationId, clientName, onScheduled }) {
  const [start, setStart] = useState(() => toLocalDatetimeValue(defaultSlot()));
  const [durationMin, setDurationMin] = useState(50);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const startDate = new Date(start);
      if (isNaN(startDate.getTime())) throw new Error('Invalid start time');
      const endDate = new Date(startDate.getTime() + durationMin * 60 * 1000);
      const { appointment } = await createAppointment({
        token,
        clientId,
        conversationId,
        scheduledStart: startDate.toISOString(),
        scheduledEnd:   endDate.toISOString(),
        notes
      });
      onScheduled?.(appointment);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.msg || err.message || 'Could not schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule video call{clientName ? ` with ${clientName}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The client will be notified and can join from their dashboard when it's time.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Start"
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Duration (minutes)"
            type="number"
            inputProps={{ min: 15, max: 180, step: 5 }}
            value={durationMin}
            onChange={(e) => setDurationMin(parseInt(e.target.value, 10) || 50)}
            fullWidth
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            placeholder="Anything to remember about this session."
          />
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !start}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {saving ? 'Scheduling…' : 'Schedule call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
